import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';

/**
 * Selector de plan con deslizamiento horizontal.
 *
 * Es el bloque «DESBLOQUEA TODOS LOS BENEFICIOS» de los mockups: una superficie
 * de vidrio con el botón de acción y el precio debajo, y se desliza de lado
 * para pasar de anual a mensual.
 *
 * Los precios NO están escritos aquí. Vienen de `GET /api/subscription/plans`,
 * que es la misma fuente que usa el cobro — así, el día que Iván cambie el
 * precio desde el admin, esta pantalla cambia sola y no queda un número viejo
 * pegado en el código.
 *
 * `planSugerido` viene del ahorro del usuario (Módulo 4, precio ancla
 * dinámico): si ya ahorró más que la anualidad, arranca en el anual.
 */
export function PlanSwipe({ planSugerido = 'annual', onElegir, etiquetaBoton = 'DESBLOQUEA TODAS LAS MARCAS' }) {
  const [planes, setPlanes] = useState([]);
  const [indice, setIndice] = useState(0);
  const pistaRef = useRef(null);

  useEffect(() => {
    let vigente = true;
    fetch(`${API_BASE_URL}/api/subscription/plans`)
      .then((r) => r.json())
      .then((r) => {
        if (!vigente || !r?.data?.plans) return;
        const lista = r.data.plans;
        setPlanes(lista);
        const i = lista.findIndex((p) => p.key === planSugerido);
        setIndice(i >= 0 ? i : 0);
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, [planSugerido]);

  /* Coloca la pista en el plan sugerido sin animar la primera vez. */
  useEffect(() => {
    const el = pistaRef.current;
    if (!el || !planes.length) return;
    el.scrollTo({ left: indice * el.clientWidth, behavior: 'auto' });
    // Solo al cargar los planes: después manda el dedo del usuario.
    // eslint-disable-next-line
  }, [planes.length]);

  const alDeslizar = () => {
    const el = pistaRef.current;
    if (!el || !el.clientWidth) return;
    setIndice(Math.round(el.scrollLeft / el.clientWidth));
  };

  if (!planes.length) return null;

  const plan = planes[indice] || planes[0];

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1.5px solid rgba(255,255,255,0.75)',
        borderRadius: 26,
        padding: '20px 16px 14px',
        boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.65), 0 10px 30px -10px rgba(250,77,10,0.22)',
      }}
    >
      <p
        className="font-display"
        style={{
          margin: '0 0 14px',
          textAlign: 'center',
          fontSize: 15,
          fontWeight: 600,
          color: '#33333A',
          letterSpacing: '-0.01em',
        }}
      >
        DESBLOQUEA <span style={{ color: '#6E6E77', fontWeight: 500 }}>TODOS LOS BENEFICIOS</span>
      </p>

      {/* Una "página" por plan; el scroll con imán hace el deslizamiento. */}
      <div
        ref={pistaRef}
        onScroll={alDeslizar}
        className="mp-no-scrollbar"
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
        }}
      >
        {planes.map((p) => (
          <div key={p.key} style={{ flex: '0 0 100%', scrollSnapAlign: 'center', padding: '0 4px' }}>
            <button
              type="button"
              onClick={() => onElegir?.(p)}
              className="bg-grad-rojo-rosa text-white rounded-full font-semibold"
              style={{
                width: '100%',
                height: 46,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                letterSpacing: '0.02em',
                boxShadow: '0 8px 22px -8px rgba(245,43,22,0.5)',
              }}
            >
              {etiquetaBoton}
            </button>
            {/* Punto medio, no barra: es lo que dibuja `PANTALLA_PAGO` («vondi · 1 año»). */}
            <p style={{ textAlign: 'center', margin: '10px 0 0', fontSize: 14, color: '#F73E48', fontWeight: 600 }}>
              ${p.amount.toLocaleString('es-MX')} · {p.interval === 'year' ? 'año' : 'mes'}
            </p>
          </div>
        ))}
      </div>

      {/* Indicador de página, igual que en los mockups */}
      <div style={{ display: 'flex', gap: 5, justifyContent: 'center', marginTop: 10 }}>
        {planes.map((p, i) => (
          <span
            key={p.key}
            style={{
              width: i === indice ? 18 : 6,
              height: 6,
              borderRadius: 9999,
              background: i === indice ? '#0E0E10' : '#D5D5D9',
              transition: 'width .2s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default PlanSwipe;
